import { nanoid, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

import {
  releaseStructuralWrite,
  StructuralWriteToken,
  tryAcquireStructuralWrite,
} from '@/shared/lib/transcriptVersionWriteLock';
import { TEditorHistoryKind } from '@/shared/types/Editor';

import { RootState } from '../../index';
import { isEditorMutationBlocked } from '../editorSelectors';
import {
  commitEditorHistoryAction,
  releaseEditorOperation as releaseEditorOperationById,
  tryAcquireEditorOperation,
} from '../EditorSlice';

declare const editorOperationTokenBrand: unique symbol;

export type TEditorOperationToken = Readonly<{ [editorOperationTokenBrand]: true }>;

type TEditorThunkDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

const operationsByToken = new WeakMap<
  TEditorOperationToken,
  { operationId: string; structuralWriteToken: StructuralWriteToken }
>();

const getOwnedOperationId = (state: RootState, operationToken: TEditorOperationToken): null | string => {
  const registeredOperationId = operationsByToken.get(operationToken)?.operationId;

  return registeredOperationId && state.editor.activeEditorOperationId === registeredOperationId
    ? registeredOperationId
    : null;
};

export const getEditorOperationStructuralWriteToken = (
  state: RootState,
  operationToken: TEditorOperationToken,
): null | StructuralWriteToken => {
  if (!getOwnedOperationId(state, operationToken)) {
    return null;
  }

  return operationsByToken.get(operationToken)?.structuralWriteToken || null;
};

export const acquireEditorOperation = (projectId: string, historyKind?: TEditorHistoryKind) => {
  return (dispatch: TEditorThunkDispatch, getState: () => RootState): null | TEditorOperationToken => {
    const structuralWriteToken = tryAcquireStructuralWrite(projectId);

    if (!structuralWriteToken) {
      return null;
    }

    const operationId = nanoid();
    const operationToken = Object.freeze({}) as TEditorOperationToken;
    operationsByToken.set(operationToken, { operationId, structuralWriteToken });

    dispatch(tryAcquireEditorOperation({ historyKind, operationId }));

    if (getState().editor.activeEditorOperationId === operationId) {
      return operationToken;
    }

    operationsByToken.delete(operationToken);
    releaseStructuralWrite(structuralWriteToken);
    return null;
  };
};

export const commitEditorOperationHistory = (operationToken: TEditorOperationToken) => {
  return (dispatch: TEditorThunkDispatch, getState: () => RootState): boolean => {
    const operationId = getOwnedOperationId(getState(), operationToken);

    if (!operationId) {
      return false;
    }

    dispatch(commitEditorHistoryAction({ operationId }));
    return true;
  };
};

export const releaseEditorOperation = (operationToken: TEditorOperationToken) => {
  return (dispatch: TEditorThunkDispatch, getState: () => RootState): boolean => {
    const operationId = getOwnedOperationId(getState(), operationToken);
    const operation = operationsByToken.get(operationToken);
    operationsByToken.delete(operationToken);

    if (operation) {
      releaseStructuralWrite(operation.structuralWriteToken);
    }

    if (!operationId) {
      return false;
    }

    dispatch(releaseEditorOperationById({ operationId }));
    return true;
  };
};

export const editorOperationAllowsMutation = (state: RootState, operationToken?: TEditorOperationToken): boolean => {
  const ownedOperationId = operationToken ? getOwnedOperationId(state, operationToken) : null;

  if (isEditorMutationBlocked(state, ownedOperationId || undefined)) {
    return false;
  }

  return state.editor.activeEditorOperationId ? Boolean(ownedOperationId) : operationToken === undefined;
};
